import { Book, BookOpen, GraduationCap, PlayCircle, Trophy } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { useDashboard } from '@/hooks/use-dashboard'
import { useTenantPath } from '@/lib/tenant'

interface InProgressCourse {
  course: { id: string; title: string }
  progress: { percentage: number; completed: number; total: number }
  currentLesson: { currentLesson: { id: string; title: string } | null } | null
}

interface CompletedCourse {
  course: { id: string; title: string }
  progress: { percentage: number; completed: number; total: number }
}

interface RecentCompletion {
  id: string
  lessonId: string
  completedAt: string
}

interface DashboardData {
  inProgress: InProgressCourse[]
  completed: CompletedCourse[]
  recentCompletions: RecentCompletion[]
  stats: {
    completedCourses: number
    inProgressCourses: number
    totalEnrollments: number
  }
}

export default function DashboardPage() {
  const { data, isLoading } = useDashboard()
  const tp = useTenantPath()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">ダッシュボード</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {['skel-1', 'skel-2', 'skel-3'].map((id) => (
            <Skeleton key={id} className="h-32" />
          ))}
        </div>
      </div>
    )
  }

  const dashboard = data as DashboardData | undefined

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">ダッシュボード</h1>
          <p className="text-muted-foreground mt-2">学習の進捗状況を確認しましょう</p>
        </div>
        <Link to={tp('/manual')}>
          <Button variant="outline" className="gap-2">
            <Book className="h-4 w-4" />
            操作マニュアル
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">完了したコース</CardTitle>
            <Trophy className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.stats?.completedCourses ?? 0}</div>
            <p className="text-xs text-muted-foreground">コース修了</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">受講中コース</CardTitle>
            <BookOpen className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.stats?.inProgressCourses ?? 0}</div>
            <p className="text-xs text-muted-foreground">進行中</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">総受講登録</CardTitle>
            <GraduationCap className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{dashboard?.stats?.totalEnrollments ?? 0}</div>
            <p className="text-xs text-muted-foreground">コース登録</p>
          </CardContent>
        </Card>
      </div>

      {/* In-progress courses */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">進行中のコース</h2>
        {!dashboard?.inProgress?.length ? (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">進行中のコースはありません</p>
              <Link to={tp('/courses')}>
                <Button className="mt-4">コースを探す</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {dashboard.inProgress.map((item) => (
              <Card key={item.course.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{item.course.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>進捗</span>
                      <span>{item.progress.percentage}%</span>
                    </div>
                    <Progress value={item.progress.percentage} className="h-2" />
                  </div>
                  {item.currentLesson?.currentLesson ? (
                    <Link to={tp(`/learn/${item.course.id}`)}>
                      <Button className="w-full">学習を続ける</Button>
                    </Link>
                  ) : (
                    <Link to={tp(`/courses/${item.course.id}`)}>
                      <Button variant="outline" className="w-full">
                        コース詳細
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Completed courses */}
      {dashboard?.completed && dashboard.completed.length > 0 && (
        <div>
          <h2 className="text-2xl font-semibold mb-4">完了したコース</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {dashboard.completed.map((item) => (
              <Card key={item.course.id} className="relative">
                <div className="absolute top-4 right-4">
                  <Badge className="bg-green-600 text-white hover:bg-green-600">完了</Badge>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">{item.course.title}</CardTitle>
                  <CardDescription>
                    {item.progress.completed}/{item.progress.total} レッスン完了
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Link to={tp(`/courses/${item.course.id}`)}>
                    <Button variant="outline" className="w-full">
                      復習する
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recent activity */}
      <div>
        <h2 className="text-2xl font-semibold mb-4">最近の活動</h2>
        <Card>
          <CardContent className="p-0">
            {!dashboard?.recentCompletions?.length ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">まだ活動がありません</p>
              </div>
            ) : (
              <div className="divide-y">
                {dashboard.recentCompletions.map((completion) => (
                  <div key={completion.id} className="flex items-start space-x-4 p-4">
                    <div className="bg-blue-100 rounded-full p-2">
                      <PlayCircle className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">レッスン完了</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(completion.completedAt).toLocaleDateString('ja-JP', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
